import React, { useState, useEffect } from "react";
import {
  FiThumbsUp,
  FiThumbsDown,
  FiCopy,
  FiMessageSquare,
  FiCheck,
} from "react-icons/fi";
import { LEVEL_DATA } from "./AssistanceLevelSelector";
import { formatSolutionLevelLabel } from "../utils/solutionLabels";
import "./MessageFeedback.css";
import { getApiUrl } from "../utils/api";

const MessageFeedback = ({ message, sessionId, messageIndex, responseId }) => {
  const [feedback, setFeedback] = useState(null); // 'up', 'down', or null
  const [showTextFeedback, setShowTextFeedback] = useState(false);
  const [textFeedback, setTextFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Fetch existing feedback on component mount
  useEffect(() => {
    if (!sessionId) return;  // Do not fetch feedback without a valid session
    const fetchFeedback = async () => {
      try {
        const response = await fetch(getApiUrl("/api/feedback/get"), {
          method: "POST",
          credentials: 'include',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId, // Use session_id instead of session_name
            message_index: messageIndex,
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.feedback_type) {
            setFeedback(data.feedback_type);
            setTextFeedback(data.text_feedback || "");
          }
        }
      } catch (error) {
        console.error("Error fetching feedback:", error);
      }
    };

    fetchFeedback();
  }, [sessionId, messageIndex]);

  const handleThumbsClick = async (type) => {
    const newFeedback = feedback === type ? null : type;
    setFeedback(newFeedback);

    try {
      await fetch(getApiUrl("/api/feedback/submit"), {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId, // Use session_id instead of session_name
          message_index: messageIndex,
          feedback_type: newFeedback,
          text_feedback: textFeedback,
          response_id: responseId,
        }),
      });
    } catch (error) {
      console.error("Error submitting feedback:", error);
      // Revert on error
      setFeedback(feedback);
    }
  };

  const handleTextFeedbackSubmit = async () => {
    setLoading(true);
    try {
      await fetch(getApiUrl("/api/feedback/submit"), {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId, // Use session_id instead of session_name
          message_index: messageIndex,
          feedback_type: feedback,
          text_feedback: textFeedback,
          response_id: responseId,
        }),
      });
      
      setSuccess(true);
      setShowTextFeedback(false);
      setTimeout(() => setSuccess(false), 2000);
    } catch (error) {
      console.error("Error submitting text feedback:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleTextFeedbackSubmit();
    }
  };

  // Debug: Check if assistance_level exists
  const assistanceLevel = message?.assistance_level;
  const parsedAssistanceLevel =
    typeof assistanceLevel === "string" ? parseInt(assistanceLevel, 10) : assistanceLevel;
  const levelInfo = parsedAssistanceLevel ? LEVEL_DATA[parsedAssistanceLevel] : null;

  return (
    <div className="message-feedback">
      <div className="feedback-actions">
        {levelInfo && (
          <div
            className="assistance-level-badge"
            title={levelInfo.description}
            style={{
              color: levelInfo.color,
              backgroundColor: `${levelInfo.color}15`,
              borderColor: `${levelInfo.color}30`,
            }}
          >
            <span className="badge-icon">{levelInfo.icon}</span>
            <span className="badge-text">{levelInfo.name}</span>
          </div>
        )}
        
        <button
          className={`feedback-btn ${feedback === "up" ? "active" : ""}`}
          onClick={() => handleThumbsClick("up")}
          title="Good response"
        >
          <FiThumbsUp />
        </button>
        
        <button
          className={`feedback-btn ${feedback === "down" ? "active" : ""}`}
          onClick={() => handleThumbsClick("down")}
          title="Poor response"
        >
          <FiThumbsDown />
        </button>
        
        <button
          className="feedback-btn"
          onClick={() => setShowTextFeedback(!showTextFeedback)}
          title="Add comment"
        >
          <FiMessageSquare />
        </button>
        
        <button
          className={`feedback-btn ${copySuccess ? "success" : ""}`}
          onClick={handleCopy}
          title="Copy message"
        >
          {copySuccess ? <FiCheck /> : <FiCopy />}
        </button>
      </div>

      {showTextFeedback && (
        <div className="text-feedback-section">
          <textarea
            className="text-feedback-input"
            placeholder="Tell us what you think about this response..."
            value={textFeedback}
            onChange={(e) => setTextFeedback(e.target.value)}
            onKeyDown={handleKeyPress}
            rows={3}
          />
          <div className="text-feedback-actions">
            <button
              className="submit-btn"
              onClick={handleTextFeedbackSubmit}
              disabled={loading || !textFeedback.trim()}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
            <button
              className="cancel-btn"
              onClick={() => setShowTextFeedback(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="success-message">
          Feedback submitted successfully!
        </div>
      )}
    </div>
  );
};

export default MessageFeedback;
